// SavedPriceSummary — renders the quote's most recently persisted price
// (the snapshot stored after the last ERP call) as an itemized table, with
// finishing operations shown as indented sub-lines under their parent
// product line.
//
// This is display continuity between page loads. On reload `pricingState`
// resets to idle, so the live PricingBlock isn't shown; this surfaces the
// saved figures instead. The authoritative price is always a fresh "See my
// price" call (which renders PricingBlock). Per CLAUDE.md this snapshot is
// NEVER used in place of a live call at decision points — submit and
// checkout re-price server-side.
//
// Why sub-lines matter here: Nexus prices each finishing op, and each line it
// derives itself (NCR pad glue, pallet prep), as its own line, so
// total_sale_amount includes them. The product lines alone would sum to LESS
// than the total; rendering each line plus its sub-lines makes the itemized view
// reconcile.

import { useTranslation } from 'react-i18next';

import type { QuoteDto } from 'shared';

import { ARTWORK_PENDING_DISCLAIMER } from '../pricingCopy.js';
import { OrderLineItems } from './OrderLineItems.js';

export function SavedPriceSummary({ quote }: { quote: QuoteDto }): JSX.Element | null {
  const { t } = useTranslation(['pricing', 'common']);
  if (quote.total_sale_amount == null) return null;

  return (
    <div className="card pricing-block">
      <h2>{t('saved.title')}</h2>
      <p className="help" style={{ marginTop: 0 }}>
        {t('saved.description')}
      </p>
      <OrderLineItems quote={quote} />
      {quote.artwork_pending && (
        // "Quote now, art to follow" — mirrors the live PricingBlock caveat on
        // the reloaded snapshot. Nexus owns the wording (mirrored in pricingCopy).
        <p className="help" style={{ marginTop: 12, fontStyle: 'italic' }}>
          {ARTWORK_PENDING_DISCLAIMER}
        </p>
      )}
    </div>
  );
}
